import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../lib/api';
import { textoDeError } from './Dialogo';
import { Recortador, ImgRecortada as ImgRecortadaG, abrirArchivo, generarJpeg, recInicial, cajaDe as cajaDeG } from './RecorteImagen';
import type { Fuente, Caja, Rec, VistaImagen } from './RecorteImagen';

/**
 * LA IMAGEN de un mensaje a la app de pacientes (0.51.0; API `avisos/imagen`, lib/avisos-imagen.ts).
 *
 * Los tamaños que existen: la imagen grande de una notificación web la enseña Chrome en Android al expandir el aviso
 * (2:1, se recomienda ~1350 × 675) y Windows (2:1, chica, ~364 × 180). macOS casi nunca, y el iPhone NUNCA (sólo título,
 * texto e icono): ahí se ve dentro de la app, en Mensajes. Así que hay UN tamaño: 2:1, y la salida es un JPEG de
 * 1440 × 720 (≤ ~350 KB) que sirve para los tres lugares.
 *
 * Cómo: se elige un archivo (JPG, PNG, WebP; HEIC si el navegador la abre, y si no se intenta convertirla aquí), se
 * enseña COMPLETA con un cuadro 2:1 encima que se arrastra para elegir qué parte queda y un control para acercar, y "Usar
 * esta imagen" la recorta en un canvas, la reduce y la sube. Sin librerías: el canvas del navegador.
 */
export const IMG_ANCHO = 1440;
export const IMG_ALTO = 720;
export const IMG_MAX_KB = 350;
const ASPECTO = 2;

export type ImagenAviso = { ruta: string; url: string | null; local: string | null; ancho: number; alto: number; bytes: number };
export type { Fuente, Caja, VistaImagen };
export type EstadoImagen = { disponible: boolean; motivo: string | null; almacen_app?: boolean | null };

/** La parte que queda de una foto con el cuadro 2:1 (lo usa la prueba de recorte). */
export const cajaDe = (f: Fuente, r: Rec): Caja => cajaDeG(f, r, ASPECTO);

const btn: CSSProperties = { padding: '6px 14px', fontSize: 13 };

/**
 * El campo "Imagen (opcional)". `valor` = la imagen ya subida (o null). `onVista` = lo que deben enseñar las vistas previas
 * en este momento (el recorte en vivo mientras se elige). `onOcupado` = true mientras se recorta o se sube (no se manda).
 * El recorte lo hace `RecorteImagen.tsx` (el mismo de Promociones), con el cuadro 2:1.
 */
export function ImagenAvisoCampo({ valor, onCambio, estado, onVista, onOcupado, datos = 'av' }: {
  valor: ImagenAviso | null; onCambio: (v: ImagenAviso | null) => void; estado: EstadoImagen | null | undefined;
  onVista: (v: VistaImagen) => void; onOcupado?: (x: boolean) => void; datos?: string;
}) {
  const [fuente, setFuente] = useState<Fuente | null>(null);
  const [rec, setRec] = useState<Rec | null>(null);
  const [editando, setEditando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [abriendo, setAbriendo] = useState(false);
  const [error, setError] = useState('');
  const archivo = useRef<HTMLInputElement>(null);
  const antes = useRef<ImagenAviso | null>(valor);

  // Si quien la usa la borra (el mensaje se mandó), se olvida también el archivo de origen.
  useEffect(() => {
    if (antes.current && !valor && !editando) { setFuente(null); setRec(null); setError(''); }
    antes.current = valor;
  }, [valor]);
  useEffect(() => { onOcupado?.(editando || subiendo || abriendo); }, [editando, subiendo, abriendo]);
  useEffect(() => {
    if (editando && fuente && rec) onVista({ tipo: 'recorte', fuente, caja: cajaDe(fuente, rec) });
    else if (valor) onVista({ tipo: 'url', url: valor.url || valor.local || '', respaldo: valor.local });
    else onVista(null);
  }, [editando, fuente, rec, valor]);

  async function elegido(f: File | undefined) {
    if (!f) return;
    setError(''); setAbriendo(true);
    try {
      const fu = await abrirArchivo(f);
      if (fu.w < 360 || fu.h < 180) throw new Error(`Esa imagen mide ${fu.w} × ${fu.h}: tiene que medir al menos 360 × 180 (mejor 1440 × 720 o más).`);
      setFuente(fu); setRec(recInicial(fu)); setEditando(true);
    } catch (e: any) { setError(e?.message || 'No se pudo abrir esa imagen.'); }
    finally { setAbriendo(false); if (archivo.current) archivo.current.value = ''; }
  }
  async function usar() {
    if (!fuente || !rec) return;
    setSubiendo(true); setError('');
    try {
      const { data } = await generarJpeg(fuente, cajaDe(fuente, rec), { ancho: IMG_ANCHO, alto: IMG_ALTO, maxKB: IMG_MAX_KB });
      const r = await api<any>('avisos/imagen', { method: 'POST', body: JSON.stringify({ data }) });
      onCambio({ ruta: r.imagen, url: r.imagen_url || null, local: data, ancho: r.ancho, alto: r.alto, bytes: r.bytes });
      setEditando(false);
    } catch (e: any) { setError(textoDeError(e, 'No se pudo subir la imagen.')); }
    finally { setSubiendo(false); }
  }
  function cancelar() { setEditando(false); setError(''); if (!valor) { setFuente(null); setRec(null); } }

  const disponible = !estado || estado.disponible;
  const c = fuente && rec ? cajaDe(fuente, rec) : null;
  return (
    <div style={{ display: 'grid', gap: 8 }} data-av-img={datos}>
      <input ref={archivo} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" style={{ display: 'none' }} onChange={(e) => elegido(e.target.files?.[0])} data-av-img-archivo aria-label="Elegir imagen" />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
        <strong style={{ fontSize: 13 }}>Imagen <span className="muted" style={{ fontWeight: 400 }}>(opcional)</span></strong>
        {!editando && !valor && <button type="button" className="btn-ghost" style={btn} disabled={!disponible || abriendo} onClick={() => archivo.current?.click()} data-av-img-elegir>{abriendo ? 'Abriendo…' : 'Elegir imagen'}</button>}
        {!editando && valor && <>
          <span className="muted" style={{ fontSize: 12 }} data-av-img-lista>{valor.ancho} × {valor.alto} · {Math.round(valor.bytes / 1024)} KB · lista</span>
          {fuente && <button type="button" className="btn-ghost" style={btn} onClick={() => setEditando(true)} data-av-img-recortar>Cambiar recorte</button>}
          <button type="button" className="btn-ghost" style={btn} disabled={abriendo} onClick={() => archivo.current?.click()} data-av-img-cambiar>{abriendo ? 'Abriendo…' : 'Cambiar imagen'}</button>
          <button type="button" className="btn-ghost" style={{ ...btn, color: 'var(--danger)' }} onClick={() => { onCambio(null); setFuente(null); setRec(null); }} data-av-img-quitar>Quitar imagen</button>
        </>}
      </div>
      {!disponible && <span style={{ fontSize: 12, color: 'var(--warning)' }} data-av-img-no>{estado?.motivo}</span>}
      {disponible && estado?.almacen_app === false && <span style={{ fontSize: 12, color: 'var(--warning)' }} data-av-img-sin-almacen-app>La app de pacientes no tiene el almacén configurado (AZURE_STORAGE_CONNECTION_STRING en su SWA): el mensaje saldría sin la imagen.</span>}
      {!editando && !valor && disponible && <span className="muted" style={{ fontSize: 12 }}>Se recorta a 2:1 (1440 × 720). La enseñan las notificaciones de Android y Windows y el mensaje dentro de la app.</span>}
      {editando && fuente && rec && c && (
        <Recortador fuente={fuente} rec={rec} onRec={setRec} aspecto={ASPECTO} maxAlto={typeof window !== 'undefined' && window.innerWidth < 600 ? 240 : 320} />
      )}
      {editando && c && c.w < IMG_ANCHO * 0.6 && <span style={{ fontSize: 12, color: 'var(--warning)' }} data-av-img-borrosa>La parte elegida mide {Math.round(c.w)} px de ancho: en un teléfono grande puede verse borrosa. Aleja un poco o usa una imagen más grande.</span>}
      {editando && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn-primary" style={btn} disabled={subiendo} onClick={usar} data-av-img-usar>{subiendo ? 'Subiendo…' : 'Usar esta imagen'}</button>
        <button type="button" className="btn-ghost" style={btn} disabled={subiendo} onClick={cancelar} data-av-img-cancelar>Cancelar</button>
      </div>}
      {error && <span style={{ fontSize: 12.5, color: 'var(--danger)' }} role="alert" data-av-img-error>{error}</span>}
    </div>
  );
}

/** La imagen 2:1 de una vista previa: la guardada (url) o el recorte en vivo. */
export function ImgRecortada({ vista, style, datos }: { vista: VistaImagen; style?: CSSProperties; datos?: string }) {
  return <ImgRecortadaG vista={vista} aspecto={ASPECTO} style={style} datos={datos} ancho={IMG_ANCHO} alto={IMG_ALTO} />;
}

const CAT_APP: Record<string, string> = { promos: 'Promoción', clinica: 'De la clínica', citas: 'Citas', puntos: 'Puntos y premios', plan: 'Mi plan', tips: 'Consejo' };
const ACC_APP: Record<string, string> = { promos: 'Ver la promoción', tarjeta: 'Ver mi tarjeta', tienda: 'Ir a la tienda', recompensas: 'Ver recompensas', citas: 'Agendar', plan: 'Ver mi plan', transformacion: 'Ver mi transformación' };

/**
 * Cómo se ve el mensaje en los tamaños que existen: la notificación de Android al expandirla (imagen 2:1 grande), la de
 * Windows (2:1 chica, arriba) y dentro de la app (la campana → Mensajes). En iPhone la notificación no enseña imagen.
 */
export function VistasAviso({ titulo, cuerpo, vista, categoria, accion }: { titulo: string; cuerpo: string; vista: VistaImagen; categoria: string; accion: string }) {
  const t = titulo.trim() || 'Título del mensaje'; const b = cuerpo.trim() || 'El mensaje';
  const fig: CSSProperties = { margin: 0, display: 'grid', gap: 6, alignContent: 'start', minWidth: 0 };
  const cap: CSSProperties = { fontSize: 12, color: 'var(--ink-soft)', fontWeight: 600 };
  const icono = <img src="/icons/DTB_1-192.png" alt="" width={18} height={18} style={{ borderRadius: '50%', flex: '0 0 auto' }} onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />;
  return (
    <div style={{ display: 'grid', gap: 10 }} data-av-vistas>
      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', alignItems: 'start' }}>
        <figure style={fig} data-av-vista="android">
          <figcaption style={cap}>Android · al expandir la notificación</figcaption>
          <div style={{ background: '#fff', borderRadius: 20, padding: '12px 14px', boxShadow: '0 2px 10px rgba(0,0,0,.12)', maxWidth: 380, display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, color: '#5f5f5f' }}>{icono}<span>Safevity Rewards · ahora</span><span style={{ marginLeft: 'auto' }} aria-hidden>⌃</span></div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1f1f1f', overflowWrap: 'anywhere' }}>{t}</div>
            <div style={{ fontSize: 13, color: '#444', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{b}</div>
            {vista && <div style={{ borderRadius: 12, overflow: 'hidden', marginTop: 2 }}><ImgRecortada vista={vista} datos="android" /></div>}
          </div>
        </figure>
        <figure style={fig} data-av-vista="windows">
          <figcaption style={cap}>Windows · Chrome o Edge</figcaption>
          <div style={{ background: '#2b2b2b', color: '#fff', borderRadius: 8, overflow: 'hidden', maxWidth: 364, boxShadow: '0 4px 14px rgba(0,0,0,.25)' }}>
            {vista && <ImgRecortada vista={vista} datos="windows" />}
            <div style={{ padding: '10px 12px', display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, color: '#cfcfcf' }}>{icono}<span>Safevity Rewards</span></div>
              <div style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: 'anywhere' }}>{t}</div>
              <div style={{ fontSize: 12.5, color: '#dcdcdc', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{b}</div>
              <div style={{ fontSize: 11, color: '#a8a8a8' }}>pts-users.duenadetubelleza.com</div>
            </div>
          </div>
        </figure>
        <figure style={fig} data-av-vista="app">
          <figcaption style={cap}>Dentro de la app · Mensajes</figcaption>
          <div style={{ background: '#fffafc', border: '1px solid #8E1A5F', boxShadow: '0 0 0 1px rgba(142, 26, 95, 0.15)', borderRadius: 14, overflow: 'hidden', maxWidth: 380 }}>
            {vista && <ImgRecortada vista={vista} datos="app" />}
            <div style={{ padding: '10px 14px 12px', display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, color: '#888' }}>
                <span style={{ textTransform: 'uppercase', letterSpacing: 0.5, color: '#8b7355', fontWeight: 600 }}>{CAT_APP[categoria] || 'Mensaje'}</span>
                <span style={{ marginLeft: 'auto' }}>Hoy</span>
                <span style={{ background: '#8E1A5F', color: '#fff', borderRadius: 8, padding: '1px 7px', fontWeight: 700, fontSize: 10.5 }}>Nuevo</span>
              </div>
              <div style={{ fontWeight: 600, fontSize: 14.5, color: '#3b342b', overflowWrap: 'anywhere' }}>{t}</div>
              <div style={{ fontSize: 13, color: '#554c41', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{b}</div>
              <span style={{ justifySelf: 'start', marginTop: 4, padding: '6px 14px', borderRadius: 999, border: '1px solid #ac996c', color: '#8b7355', fontSize: 12.5 }}>{ACC_APP[accion] || 'Ver'}</span>
            </div>
          </div>
        </figure>
      </div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink)' }} data-av-iphone>
        <strong>En iPhone la notificación no enseña imagen; se ve dentro de la app, en Mensajes.</strong>{' '}
        <span className="muted">En una Mac casi nunca sale. En Android se ve al expandir el aviso.</span>
      </p>
    </div>
  );
}
